/**
 * ProseContainer - Canonical reading surface wrapper
 *
 * Enforces consistent typography and reading width across all
 * long-form content pages (Goals, Numbers, Memories) to match
 * the Advisor active chat canvas.
 *
 * Typography contract:
 * - H1/H2/H3: Playfair Display (font-display)
 * - Body paragraphs: Source Serif 4 (font-serif)
 * - UI/meta text: Inter (font-sans)
 * - Max reading width: 72ch
 * - Consistent vertical rhythm: space-y-8
 *
 * NOTE: Horizontal padding is handled by CenterColumn wrapper,
 * ProseContainer only handles max-width and vertical rhythm.
 */

import { cn } from "@/lib/utils"
import type { ReactNode } from "react"

interface ProseContainerProps {
  children: ReactNode
  className?: string
}

export function ProseContainer({ children, className }: ProseContainerProps) {
  return (
    <div
      className={cn(
        // Canonical reading width matching advisor-prose
        "max-w-[72ch] w-full mx-auto",
        // Consistent vertical rhythm
        "space-y-8",
        // Only vertical padding for breathing room
        "pt-8 pb-8",
        className,
      )}
    >
      {children}
    </div>
  )
}

/**
 * ProseTitle - Page title with Playfair Display (30px)
 * Use for main page titles in reading surfaces
 */
interface ProseTitleProps {
  children: ReactNode
  className?: string
}

export function ProseTitle({ children, className }: ProseTitleProps) {
  return (
    <h1 className={cn("font-display font-normal text-foreground text-[30px] leading-[1.27]", className)}>{children}</h1>
  )
}

/**
 * ProseSubtitle - Subtitle with muted styling
 * Use below page titles for context
 */
interface ProseSubtitleProps {
  children: ReactNode
  className?: string
}

export function ProseSubtitle({ children, className }: ProseSubtitleProps) {
  return <p className={cn("text-muted-foreground font-sans text-base mt-2", className)}>{children}</p>
}

/**
 * ProseHeading - Section heading with Playfair Display
 * Use for H1/H2/H3 in reading surfaces
 */
interface ProseHeadingProps {
  as?: "h1" | "h2" | "h3" | "h4"
  children: ReactNode
  className?: string
}

export function ProseHeading({ as: Component = "h2", children, className }: ProseHeadingProps) {
  const baseStyles = "font-display font-normal text-foreground"

  const sizeStyles = {
    h1: "text-[30px] leading-[1.27]",
    h2: "text-[24px] leading-[1.33]",
    h3: "text-[22px] leading-[1.36]",
    h4: "text-[20px] leading-[1.4]",
  }

  return <Component className={cn(baseStyles, sizeStyles[Component], className)}>{children}</Component>
}

/**
 * ProseBody - Body paragraph with Source Serif 4
 * Use for explanatory text in reading surfaces
 */
interface ProseBodyProps {
  children: ReactNode
  className?: string
  muted?: boolean
}

export function ProseBody({ children, className, muted = true }: ProseBodyProps) {
  return (
    <p
      className={cn(
        "font-serif text-[18px] leading-[1.65]",
        muted ? "text-muted-foreground" : "text-foreground",
        className,
      )}
    >
      {children}
    </p>
  )
}

/**
 * ProseSection - Grouped heading + body content
 * Provides consistent spacing between sections
 */
interface ProseSectionProps {
  children: ReactNode
  className?: string
}

export function ProseSection({ children, className }: ProseSectionProps) {
  return <div className={cn("space-y-3", className)}>{children}</div>
}
